import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mic, MicOff } from "lucide-react";
import VoiceOrb from "./VoiceOrb";
import MatrixRain from "./MatrixRain";
import SofiaWakeUp from "./SofiaWakeUp";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Fullscreen Siri-like voice interface for Sofia
 * Direct voice conversation with visual feedback
 */
export default function SiriMode({ sessionId, onClose }) {
  const [wakeUpComplete, setWakeUpComplete] = useState(false);
  const [state, setState] = useState("idle"); // idle, listening, speaking
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [amplitude, setAmplitude] = useState(0.5);

  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  // Initialize Web Speech API
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice not supported in this browser");
      return;
    }

    const recog = new SR();
    recog.lang = "en-US";
    recog.continuous = false;
    recog.interimResults = true;

    recog.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setTranscript(transcript);

      // Final result - send to Sofia
      if (e.results[0].isFinal) {
        sendMessage(transcript);
      }
    };

    recog.onend = () => {
      if (state === "listening") {
        setState("idle");
      }
    };

    recog.onerror = (e) => {
      console.warn("Speech recognition error:", e.error);
      if (e.error !== "no-speech") {
        setState("idle");
      }
    };

    recogRef.current = recog;

    return () => {
      recog.stop();
    };
  }, []);

  // Start listening
  const startListening = () => {
    if (recogRef.current && state !== "speaking") {
      setTranscript("");
      setState("listening");
      recogRef.current.start();
    }
  };

  // Stop listening
  const stopListening = () => {
    if (recogRef.current) {
      recogRef.current.stop();
      setState("idle");
    }
  };

  // Send message to Sofia
  const sendMessage = async (text) => {
    if (!text.trim()) return;

    setState("idle");
    setTranscript("");

    try {
      const { data } = await api.post("/sofia/chat", {
        session_id: sessionId,
        message: text,
        lang: "en",
      });

      setResponse(data.reply);

      // Speak the response
      speakResponse(data.reply);
    } catch (e) {
      toast.error("Sofia is temporarily unavailable");
      setState("idle");
    }
  };

  // Speak Sofia's response
  const speakResponse = async (text) => {
    if (!text) return;

    try {
      setState("speaking");

      const { data } = await api.post("/sofia/voice", { text: text.slice(0, 500) });

      if (data.audio) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))],
          { type: "audio/mpeg" }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Setup audio analyser for waveform visualization
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 256;
        }

        const source = audioContextRef.current.createMediaElementSource(audio);
        source.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);

        // Update amplitude during playback
        const updateAmplitude = () => {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAmplitude(avg / 255);

          if (audio.currentTime < audio.duration) {
            requestAnimationFrame(updateAmplitude);
          }
        };

        audio.onplay = () => updateAmplitude();

        audio.onended = () => {
          setState("idle");
          setResponse("");
          URL.revokeObjectURL(audioUrl);

          // Auto-start listening after response (hands-free mode)
          setTimeout(() => startListening(), 500);
        };

        audio.onerror = () => {
          setState("idle");
          toast.error("Audio playback failed");
        };

        await audio.play();
      }
    } catch (e) {
      console.warn("Voice playback failed:", e);
      setState("idle");
    }
  };

  // Keyboard shortcut: Space to talk
  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === "Space" && state === "idle") {
        e.preventDefault();
        startListening();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [state]);

  // Show wake-up sequence first
  if (!wakeUpComplete) {
    return <SofiaWakeUp onComplete={() => setWakeUpComplete(true)} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] bg-gradient-to-br from-ink via-ink to-ink-light flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Matrix Rain Background */}
      <MatrixRain />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-8 right-8 text-bone/60 hover:text-gold transition-colors p-2"
        aria-label="Close Siri mode"
      >
        <X size={28} />
      </button>

      {/* Top text - Status */}
      <div className="absolute top-20 left-0 right-0 text-center">
        <motion.p
          className="text-gold/80 text-sm tracking-widest uppercase font-medium"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {state === "listening" ? "Listening..." :
           state === "speaking" ? "Sofia is speaking..." :
           "Tap to talk"}
        </motion.p>
      </div>

      {/* Voice Orb */}
      <div className="w-80 h-80 sm:w-96 sm:h-96">
        <VoiceOrb state={state} amplitude={amplitude} />
      </div>

      {/* Transcript / Response Display */}
      <div className="absolute bottom-32 left-0 right-0 px-8 max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {transcript && state === "listening" && (
            <motion.div
              key="transcript"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-ink-light/50 backdrop-blur-sm border border-gold/20 rounded-2xl px-6 py-4"
            >
              <p className="text-bone text-lg leading-relaxed">
                {transcript}
              </p>
            </motion.div>
          )}

          {response && state === "speaking" && (
            <motion.div
              key="response"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gold/10 backdrop-blur-sm border border-gold/30 rounded-2xl px-6 py-4"
            >
              <p className="text-gold text-lg leading-relaxed">
                {response}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mic button */}
      <div className="absolute bottom-12">
        <motion.button
          onClick={state === "listening" ? stopListening : startListening}
          disabled={state === "speaking"}
          className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
            state === "listening"
              ? "bg-gold text-ink shadow-glow-gold"
              : "bg-ink-light border-2 border-gold/40 text-gold hover:border-gold"
          } ${state === "speaking" ? "opacity-50 cursor-not-allowed" : ""}`}
          whileTap={{ scale: 0.9 }}
          animate={{
            boxShadow:
              state === "listening"
                ? ["0 0 20px rgba(201, 168, 106, 0.5)", "0 0 40px rgba(201, 168, 106, 0.8)", "0 0 20px rgba(201, 168, 106, 0.5)"]
                : "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          {state === "listening" ? <MicOff size={32} /> : <Mic size={32} />}
        </motion.button>

        <p className="text-bone/50 text-xs text-center mt-4">
          {state === "idle" && "Press Space or tap to talk"}
        </p>
      </div>
    </motion.div>
  );
}
